import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import AuthorizationModal from "../components/AuthorizationModal";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Progress } from "../components/ui/progress";
import { Banknote, Plus, Loader2, Wallet, TrendingDown, RefreshCw, ChevronLeft, ChevronRight, AlertTriangle, Trash2, Download, Settings, Upload, Image, Tag, Pencil, X, Receipt, Calendar } from "lucide-react";
import { Link } from "react-router-dom";

const DEFAULT_CATEGORIAS = [
  { value: "transporte", label: "Transporte" },
  { value: "alimentacion", label: "Alimentación" },
  { value: "suministros", label: "Suministros de Oficina" },
  { value: "limpieza", label: "Limpieza" },
  { value: "mensajeria", label: "Mensajería" },
  { value: "servicios", label: "Servicios Varios" },
  { value: "otros", label: "Otros" }
];

export default function CajaChicaPage() {
  const [stats, setStats] = useState(null);
  const [gastosDesdeReposicion, setGastosDesdeReposicion] = useState(null);
  const [gastos, setGastos] = useState([]);
  const [categorias, setCategorias] = useState(DEFAULT_CATEGORIAS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showGastoDialog, setShowGastoDialog] = useState(false);
  const [showReposicionDialog, setShowReposicionDialog] = useState(false);
  const [vistaTab, setVistaTab] = useState("gastos");
  const [reposiciones, setReposiciones] = useState([]);
  const [editRepo, setEditRepo] = useState(null);
  const [editRepoForm, setEditRepoForm] = useState({ monto: 0, numero_cheque: "", observaciones: "" });
  const [guardandoRepo, setGuardandoRepo] = useState(false);
  const [showCategoriasDialog, setShowCategoriasDialog] = useState(false);
  const [filterCategoria, setFilterCategoria] = useState("all");
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const limit = 20;
  
  const [gastoForm, setGastoForm] = useState({ 
    fecha: new Date().toISOString().split('T')[0], 
    concepto: "", 
    categoria: "", 
    monto: 0, 
    beneficiario: "", 
    factura_numero: "", 
    descripcion: "",
    tiene_comprobante_fiscal: "no",
    comercio: "",
    ncf: "",
    transaccion: ""
  });
  const [facturaFile, setFacturaFile] = useState(null);
  const [facturaPreview, setFacturaPreview] = useState(null);
  const [reposicionForm, setReposicionForm] = useState({ monto: 0, numero_cheque: "", observaciones: "" });
  const [nuevaCategoria, setNuevaCategoria] = useState({ value: "", label: "" });
  const [editingCategoria, setEditingCategoria] = useState(null);
  
  // Authorization modal state
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [pendingDeleteGastoId, setPendingDeleteGastoId] = useState(null);
  
  // Edit gasto state
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingGasto, setEditingGasto] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editFacturaFile, setEditFacturaFile] = useState(null);
  const [editFacturaPreview, setEditFacturaPreview] = useState(null);
  const [editUploading, setEditUploading] = useState(false);
  
  const { getAuthHeaders, user } = useAuth();
  const isAdmin = user?.role?.toLowerCase() === "administrador" || user?.role?.toLowerCase() === "admin";
  const canEditCajaChica = isAdmin || user?.puede_editar_caja_chica;

  const fetchCategorias = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/caja-chica/categorias`, getAuthHeaders());
      setCategorias(res.data || DEFAULT_CATEGORIAS);
    } catch {
      setCategorias(DEFAULT_CATEGORIAS);
    }
  }, [getAuthHeaders]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      fetchCategorias();
      const [statsRes, gastosRes, gastosDesdeRepoRes] = await Promise.all([
        axios.get(`${API}/caja-chica/dashboard/stats`, getAuthHeaders()),
        axios.get(`${API}/caja-chica/gastos`, {
          ...getAuthHeaders(),
          params: { skip: page * limit, limit, categoria: filterCategoria !== "all" ? filterCategoria : undefined, fecha_desde: fechaDesde || undefined, fecha_hasta: fechaHasta || undefined }
        }),
        axios.get(`${API}/caja-chica/gastos-desde-ultima-reposicion`, getAuthHeaders())
      ]);
      setStats(statsRes.data);
      setGastos(gastosRes.data.items || []);
      setTotal(gastosRes.data.total || 0);
      setGastosDesdeReposicion(gastosDesdeRepoRes.data);
    } catch (err) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, page, filterCategoria, fechaDesde, fechaHasta, fetchCategorias]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Handle file selection for invoice
  const handleFacturaSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!["image/jpeg", "image/jpg", "image/png", "application/pdf"].includes(file.type)) {
        toast.error("Solo se permiten imágenes (JPG, PNG) o PDF");
        return;
      }
      setFacturaFile(file);
      if (file.type.startsWith("image/")) {
        setFacturaPreview(URL.createObjectURL(file));
      } else {
        setFacturaPreview(null);
      }
    }
  };

  const handleGasto = async () => {
    if (!gastoForm.concepto || !gastoForm.categoria || gastoForm.monto <= 0) { toast.error("Complete todos los campos obligatorios"); return; }
    // Validar campos de comprobante fiscal
    if (gastoForm.tiene_comprobante_fiscal === "si") {
      if (!gastoForm.comercio || !gastoForm.ncf) {
        toast.error("Complete los campos de Comercio y NCF");
        return;
      }
    }
    setSaving(true);
    try {
      // First create the expense
      const res = await axios.post(`${API}/caja-chica/gastos`, gastoForm, getAuthHeaders());
      const gastoId = res.data.id;
      
      // If there's a file, upload it
      if (facturaFile && gastoId) {
        setUploading(true);
        const formData = new FormData();
        formData.append("file", facturaFile);
        try {
          await axios.post(`${API}/caja-chica/gastos/${gastoId}/upload-factura`, formData, {
            ...getAuthHeaders(),
            headers: { ...getAuthHeaders().headers, "Content-Type": "multipart/form-data" }
          });
        } catch (uploadErr) {
          console.error("Error uploading invoice:", uploadErr);
          toast.warning("Gasto registrado pero no se pudo subir la factura");
        }
        setUploading(false);
      }
      
      toast.success("Gasto registrado");
      setShowGastoDialog(false);
      setGastoForm({ 
        fecha: new Date().toISOString().split('T')[0], 
        concepto: "", 
        categoria: "", 
        monto: 0, 
        beneficiario: "", 
        factura_numero: "", 
        descripcion: "",
        tiene_comprobante_fiscal: "no",
        comercio: "",
        ncf: "",
        transaccion: ""
      });
      setFacturaFile(null);
      setFacturaPreview(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  // Categories management
  const handleCreateCategoria = async () => {
    if (!nuevaCategoria.label) { toast.error("Ingrese el nombre de la categoría"); return; }
    try {
      await axios.post(`${API}/caja-chica/categorias`, {
        value: nuevaCategoria.label.toLowerCase().replace(/ /g, "_"),
        label: nuevaCategoria.label
      }, getAuthHeaders());
      toast.success("Categoría creada");
      setNuevaCategoria({ value: "", label: "" });
      fetchCategorias();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al crear categoría"); }
  };

  const handleDeleteCategoria = async (categoriaId) => {
    if (!window.confirm("¿Está seguro de eliminar esta categoría?")) return;
    try {
      await axios.delete(`${API}/caja-chica/categorias/${categoriaId}`, getAuthHeaders());
      toast.success("Categoría eliminada");
      fetchCategorias();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al eliminar categoría"); }
  };

  const handleReposicion = async () => {
    if (reposicionForm.monto <= 0) { toast.error("Ingrese un monto válido"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/caja-chica/reposiciones`, reposicionForm, getAuthHeaders());
      toast.success("Reposición registrada");
      setShowReposicionDialog(false);
      setReposicionForm({ monto: 0, numero_cheque: "", observaciones: "" });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const handleDeleteGasto = (id) => {
    if (!confirm("¿Eliminar este gasto? Se requerirá autorización.")) return;
    setPendingDeleteGastoId(id);
    setShowAuthModal(true);
  };

  const executeDeleteGastoWithAuth = async (authToken, authorizerName) => {
    if (!pendingDeleteGastoId) return;
    try {
      await axios.delete(`${API}/caja-chica/gastos/${pendingDeleteGastoId}?authorization_token=${authToken}`, getAuthHeaders());
      toast.success(`Gasto eliminado. Autorizado por: ${authorizerName}`);
      fetchData();
    } catch (err) { 
      toast.error(err.response?.data?.detail || "Error al eliminar"); 
    } finally {
      setPendingDeleteGastoId(null);
    }
  };

  // Edit gasto handlers
  const handleEditGasto = (gasto) => {
    setEditingGasto(gasto);
    setEditForm({
      fecha: gasto.fecha,
      concepto: gasto.concepto,
      categoria: gasto.categoria,
      monto: gasto.monto,
      beneficiario: gasto.beneficiario || "",
      factura_numero: gasto.factura_numero || "",
      descripcion: gasto.descripcion || "",
      tiene_comprobante_fiscal: gasto.tiene_comprobante_fiscal || "no",
      comercio: gasto.comercio || "",
      ncf: gasto.ncf || ""
    });
    setEditFacturaFile(null);
    setEditFacturaPreview(null);
    setShowEditDialog(true);
  };

  // Handle file selection for edit invoice
  const handleEditFacturaSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!["image/jpeg", "image/jpg", "image/png", "application/pdf"].includes(file.type)) {
        toast.error("Solo se permiten imágenes (JPG, PNG) o PDF");
        return;
      }
      setEditFacturaFile(file);
      if (file.type.startsWith("image/")) {
        setEditFacturaPreview(URL.createObjectURL(file));
      } else {
        setEditFacturaPreview(null);
      }
    }
  };

  const handleSaveEdit = async () => {
    if (!editingGasto) return;
    if (!editForm.concepto || !editForm.categoria || editForm.monto <= 0) {
      toast.error("Complete todos los campos obligatorios");
      return;
    }
    if (editForm.tiene_comprobante_fiscal === "si" && (!editForm.comercio || !editForm.ncf)) {
      toast.error("Complete los campos de Comercio y NCF");
      return;
    }
    
    setSaving(true);
    try {
      await axios.put(`${API}/caja-chica/gastos/${editingGasto.id}`, editForm, getAuthHeaders());
      
      // If there's a new file, upload it
      if (editFacturaFile) {
        setEditUploading(true);
        const formData = new FormData();
        formData.append("file", editFacturaFile);
        try {
          await axios.post(`${API}/caja-chica/gastos/${editingGasto.id}/upload-factura`, formData, {
            ...getAuthHeaders(),
            headers: { ...getAuthHeaders().headers, "Content-Type": "multipart/form-data" }
          });
        } catch (uploadErr) {
          console.error("Error uploading invoice:", uploadErr);
          toast.error("Gasto actualizado pero error al subir factura");
        }
        setEditUploading(false);
      }
      
      toast.success("Gasto actualizado correctamente");
      setShowEditDialog(false);
      setEditingGasto(null);
      setEditFacturaFile(null);
      setEditFacturaPreview(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al actualizar");
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (fechaDesde) params.append("fecha_desde", fechaDesde);
      if (fechaHasta) params.append("fecha_hasta", fechaHasta);
      params.append("tipo", "all");
      
      const response = await axios.get(`${API}/caja-chica/exportar?${params.toString()}`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `caja_chica_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Exportación completada");
    } catch (err) {
      toast.error("Error al exportar");
    }
  };

  const formatCurrency = (val) => `RD$ ${(val || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 })}`;
  const totalPages = Math.ceil(total / limit);

  const fetchReposiciones = async () => {
    try {
      const res = await axios.get(`${API}/caja-chica/reposiciones?limit=1000`, getAuthHeaders());
      setReposiciones(res.data.items || []);
    } catch (e) { toast.error("Error al cargar reposiciones"); }
  };

  useEffect(() => {
    if (vistaTab === "reposiciones" && isAdmin) fetchReposiciones();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vistaTab]);

  const guardarEdicionRepo = async () => {
    try {
      setGuardandoRepo(true);
      await axios.put(`${API}/caja-chica/reposiciones/${editRepo.id}`, editRepoForm, getAuthHeaders());
      toast.success("Reposición actualizada");
      setEditRepo(null);
      fetchReposiciones();
      fetchData();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Error al actualizar la reposición");
    } finally { setGuardandoRepo(false); }
  };

  const eliminarRepo = async (rep) => {
    if (!window.confirm(`¿Eliminar la reposición del ${rep.fecha} por ${formatCurrency(rep.monto)}? El saldo de la caja se recalculará.`)) return;
    try {
      await axios.delete(`${API}/caja-chica/reposiciones/${rep.id}`, getAuthHeaders());
      toast.success("Reposición eliminada");
      fetchReposiciones();
      fetchData();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Error al eliminar");
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="caja-chica-page">
        <div className="flex justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold dark:text-white">Caja Chica</h1>
            <p className="text-slate-500">Control de gastos menores</p>
          </div>
          <div className="flex gap-3">
            {isAdmin && (
              <>
                <Button variant="outline" onClick={() => setShowCategoriasDialog(true)}><Tag className="w-4 h-4 mr-2" />Categorías</Button>
                <Link to="/caja-chica/configuracion">
                  <Button variant="outline"><Settings className="w-4 h-4 mr-2" />Configuración</Button>
                </Link>
              </>
            )}
            <Button variant="outline" onClick={handleExport} data-testid="export-caja-chica-btn"><Download className="w-4 h-4 mr-2" />Exportar Excel</Button>
            <Button variant="outline" onClick={() => setShowReposicionDialog(true)}><RefreshCw className="w-4 h-4 mr-2" />Reponer</Button>
            <Button onClick={() => setShowGastoDialog(true)} className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"><Plus className="w-4 h-4 mr-2" />Nuevo Gasto</Button>
          </div>
        </div>

        {isAdmin && (
          <div className="flex gap-1 mb-6 border-b border-slate-200 dark:border-slate-700">
            <button
              onClick={() => setVistaTab("gastos")}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${vistaTab === "gastos" ? "border-[#0C314C] text-[#0C314C] dark:text-white" : "border-transparent text-slate-500 hover:text-slate-700"}`}
              data-testid="tab-gastos"
            >
              Gastos
            </button>
            <button
              onClick={() => setVistaTab("reposiciones")}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${vistaTab === "reposiciones" ? "border-[#0C314C] text-[#0C314C] dark:text-white" : "border-transparent text-slate-500 hover:text-slate-700"}`}
              data-testid="tab-reposiciones"
            >
              Reposiciones (Admin)
            </button>
          </div>
        )}

        {vistaTab === "gastos" && (<>
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <Card className={stats?.requiere_reposicion ? "border-amber-300 bg-amber-50" : ""}>
            <CardHeader className="pb-2"><CardTitle className="text-lg flex items-center gap-2"><Wallet className="w-5 h-5 text-[#0C314C]" />Saldo Disponible</CardTitle></CardHeader>
            <CardContent>
              <p className={`text-3xl font-bold ${stats?.requiere_reposicion ? 'text-amber-600' : 'text-[#0C314C]'}`}>{formatCurrency(stats?.saldo_actual)}</p>
              <Progress value={100 - (stats?.porcentaje_usado || 0)} className="mt-2" />
              <p className="text-sm text-slate-500 mt-1">De {formatCurrency(stats?.monto_autorizado)} autorizado</p>
              {stats?.requiere_reposicion && (
                <div className="flex items-center gap-2 mt-2 text-amber-600"><AlertTriangle className="w-4 h-4" /><span className="text-sm font-medium">Requiere reposición</span></div>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-lg flex items-center gap-2"><TrendingDown className="w-5 h-5 text-red-500" />Gastos del Mes</CardTitle></CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-red-600">{formatCurrency(stats?.total_gastos_mes)}</p>
              <div className="mt-3 space-y-1">
                {stats?.por_categoria?.slice(0, 3).map((cat, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className="text-slate-600">{categorias.find(c => c.value === cat.categoria)?.label || cat.categoria}</span>
                    <span className="font-medium">{formatCurrency(cat.total)}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          {/* Nueva tarjeta: Gastos desde última reposición */}
          <Card className="border-orange-200 bg-orange-50/30 dark:bg-orange-900/10 dark:border-orange-800/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Receipt className="w-5 h-5 text-orange-600" />
                Desde Última Reposición
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-orange-600">{formatCurrency(gastosDesdeReposicion?.total_gastos)}</p>
              {gastosDesdeReposicion?.fecha_ultima_reposicion && (
                <div className="flex items-center gap-1 text-xs text-slate-500 mt-1">
                  <Calendar className="w-3 h-3" />
                  <span>Desde: {gastosDesdeReposicion.fecha_ultima_reposicion}</span>
                </div>
              )}
              <div className="mt-3 space-y-1 max-h-24 overflow-y-auto">
                {gastosDesdeReposicion?.por_categoria?.slice(0, 5).map((cat, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className="text-slate-600 dark:text-slate-400">{categorias.find(c => c.value === cat.categoria)?.label || cat.categoria}</span>
                    <span className="font-medium text-orange-700 dark:text-orange-400">{formatCurrency(cat.total)}</span>
                  </div>
                ))}
                {(!gastosDesdeReposicion?.por_categoria || gastosDesdeReposicion.por_categoria.length === 0) && (
                  <p className="text-xs text-slate-400">Sin gastos registrados</p>
                )}
              </div>
              {gastosDesdeReposicion?.cantidad_gastos > 0 && (
                <p className="text-xs text-slate-500 mt-2">{gastosDesdeReposicion.cantidad_gastos} transacciones</p>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-lg">Resumen</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between"><span className="text-slate-600">Total Reposiciones:</span><span className="font-medium text-green-600">{formatCurrency(stats?.total_reposiciones)}</span></div>
              <div className="flex justify-between"><span className="text-slate-600">Usado:</span><span className="font-medium">{Math.round(stats?.porcentaje_usado || 0)}%</span></div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <Select value={filterCategoria} onValueChange={v => { setFilterCategoria(v); setPage(0); }}>
                <SelectTrigger><SelectValue placeholder="Categoría" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {categorias.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <div><Label className="text-xs">Desde</Label><Input type="date" value={fechaDesde} onChange={e => { setFechaDesde(e.target.value); setPage(0); }} /></div>
              <div><Label className="text-xs">Hasta</Label><Input type="date" value={fechaHasta} onChange={e => { setFechaHasta(e.target.value); setPage(0); }} /></div>
              <Button variant="ghost" onClick={() => { setFilterCategoria("all"); setFechaDesde(""); setFechaHasta(""); setPage(0); }}>Limpiar</Button>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center p-8"><Loader2 className="w-8 h-8 animate-spin" /></div>
            ) : (
              <>
                <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-slate-50 dark:bg-slate-800">
                      <th className="text-left p-4 font-medium text-slate-600">Fecha</th>
                      <th className="text-left p-4 font-medium text-slate-600">Concepto</th>
                      <th className="text-left p-4 font-medium text-slate-600">Categoría</th>
                      <th className="text-left p-4 font-medium text-slate-600">Beneficiario</th>
                      <th className="text-center p-4 font-medium text-slate-600">NCF</th>
                      <th className="text-left p-4 font-medium text-slate-600">Comercio</th>
                      <th className="text-left p-4 font-medium text-slate-600"># NCF</th>
                      <th className="text-left p-4 font-medium text-slate-600">Factura</th>
                      <th className="text-right p-4 font-medium text-slate-600">Monto</th>
                      {(isAdmin || canEditCajaChica) && <th className="text-center p-4 font-medium text-slate-600">Acciones</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {gastos.length === 0 ? (
                      <tr><td colSpan={(isAdmin || canEditCajaChica) ? 10 : 9} className="p-8 text-center text-slate-500">No hay gastos registrados</td></tr>
                    ) : gastos.map(gasto => (
                      <tr key={gasto.id} className="border-b hover:bg-slate-50">
                        <td className="p-4 whitespace-nowrap">{gasto.fecha}</td>
                        <td className="p-4"><p className="font-medium">{gasto.concepto}</p>{gasto.factura_numero && <p className="text-xs text-slate-500">Fact: {gasto.factura_numero}</p>}</td>
                        <td className="p-4"><Badge variant="outline">{categorias.find(c => c.value === gasto.categoria)?.label || gasto.categoria}</Badge></td>
                        <td className="p-4 text-slate-600">{gasto.beneficiario || "-"}</td>
                        <td className="p-4 text-center">
                          {gasto.tiene_comprobante_fiscal === "si" ? (
                            <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">Sí</Badge>
                          ) : (
                            <Badge variant="outline" className="text-slate-400">No</Badge>
                          )}
                        </td>
                        <td className="p-4 text-slate-600">{gasto.tiene_comprobante_fiscal === "si" ? gasto.comercio || "-" : "-"}</td>
                        <td className="p-4 text-slate-600 font-mono text-sm">{gasto.tiene_comprobante_fiscal === "si" ? gasto.ncf || "-" : "-"}</td>
                        <td className="p-4">
                          {gasto.factura_url ? (
                            <a href={gasto.factura_url.startsWith('/api/') ? `${API.replace('/api', '')}${gasto.factura_url}` : `${API}${gasto.factura_url}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 flex items-center gap-1">
                              <Image className="w-4 h-4" /> Ver
                            </a>
                          ) : <span className="text-slate-400 text-sm">-</span>}
                        </td>
                        <td className="p-4 text-right font-medium text-red-600 whitespace-nowrap">{formatCurrency(gasto.monto)}</td>
                        {(isAdmin || canEditCajaChica) && (
                          <td className="p-4 text-center">
                            <div className="flex items-center justify-center gap-1">
                              {canEditCajaChica && (
                                <Button variant="ghost" size="sm" className="text-blue-500 hover:text-blue-700" onClick={() => handleEditGasto(gasto)} data-testid={`edit-gasto-${gasto.id}`}>
                                  <Pencil className="w-4 h-4" />
                                </Button>
                              )}
                              {isAdmin && (
                                <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDeleteGasto(gasto.id)} data-testid={`delete-gasto-${gasto.id}`}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
                {totalPages > 1 && (
                  <div className="flex items-center justify-between p-4 border-t">
                    <p className="text-sm text-slate-500">{page * limit + 1}-{Math.min((page + 1) * limit, total)} de {total}</p>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
                      <span className="px-3 py-1">{page + 1}/{totalPages}</span>
                      <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
        </>)}

        {vistaTab === "reposiciones" && isAdmin && (
          <Card data-testid="reposiciones-admin-card">
            <CardHeader>
              <CardTitle>Historial de Reposiciones</CardTitle>
              <p className="text-sm text-slate-500">Todas las reposiciones registradas (históricas). Puedes editarlas o eliminarlas para ajustar montos mal registrados; el saldo se recalcula automáticamente.</p>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-slate-600">
                      <th className="p-3">Fecha</th>
                      <th className="p-3">Monto</th>
                      <th className="p-3">Cheque</th>
                      <th className="p-3">Observaciones</th>
                      <th className="p-3">Registrada por</th>
                      <th className="p-3">Última edición</th>
                      <th className="p-3 text-center">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reposiciones.length === 0 && (
                      <tr><td colSpan={7} className="p-8 text-center text-slate-500">No hay reposiciones registradas</td></tr>
                    )}
                    {reposiciones.map((r) => (
                      <tr key={r.id} className="border-b hover:bg-slate-50 dark:hover:bg-slate-800" data-testid={`repo-row-${r.id}`}>
                        <td className="p-3">{r.fecha}</td>
                        <td className="p-3 font-medium text-green-700">{formatCurrency(r.monto)}</td>
                        <td className="p-3">{r.numero_cheque || "—"}</td>
                        <td className="p-3 max-w-[260px] truncate" title={r.observaciones}>{r.observaciones || "—"}</td>
                        <td className="p-3 text-slate-500">{r.created_by_name || "—"}</td>
                        <td className="p-3 text-slate-500 text-xs">{r.updated_by_name ? `${r.updated_by_name} (${(r.updated_at || "").slice(0, 10)})` : "—"}</td>
                        <td className="p-3 text-center">
                          <div className="flex justify-center gap-1">
                            <Button variant="ghost" size="sm" onClick={() => { setEditRepo(r); setEditRepoForm({ monto: r.monto, numero_cheque: r.numero_cheque || "", observaciones: r.observaciones || "" }); }} data-testid={`editar-repo-${r.id}`}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600" onClick={() => eliminarRepo(r)} data-testid={`eliminar-repo-${r.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end mt-3 text-sm text-slate-600">
                Total repuesto: <span className="font-semibold ml-1 text-green-700">{formatCurrency(reposiciones.reduce((s, r) => s + (r.monto || 0), 0))}</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Editar Reposición Dialog */}
        <Dialog open={!!editRepo} onOpenChange={(v) => { if (!v) setEditRepo(null); }}>
          <DialogContent className="max-w-md" data-testid="editar-repo-dialog">
            <DialogHeader><DialogTitle>Editar Reposición — {editRepo?.fecha}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Monto *</Label><Input type="number" value={editRepoForm.monto} onChange={e => setEditRepoForm({ ...editRepoForm, monto: parseFloat(e.target.value) || 0 })} data-testid="edit-repo-monto" /></div>
              <div><Label>Número de Cheque</Label><Input value={editRepoForm.numero_cheque} onChange={e => setEditRepoForm({ ...editRepoForm, numero_cheque: e.target.value })} data-testid="edit-repo-cheque" /></div>
              <div><Label>Observaciones</Label><Textarea value={editRepoForm.observaciones} onChange={e => setEditRepoForm({ ...editRepoForm, observaciones: e.target.value })} data-testid="edit-repo-obs" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditRepo(null)}>Cancelar</Button>
              <Button onClick={guardarEdicionRepo} disabled={guardandoRepo} className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white" data-testid="guardar-edit-repo-btn">
                {guardandoRepo ? "Guardando..." : "Guardar"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Gasto Dialog */}
        <Dialog open={showGastoDialog} onOpenChange={(open) => {
          setShowGastoDialog(open);
          if (!open) { setFacturaFile(null); setFacturaPreview(null); }
        }}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle><Banknote className="w-5 h-5 inline mr-2" />Registrar Gasto</DialogTitle></DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Fecha *</Label><Input type="date" value={gastoForm.fecha} onChange={e => setGastoForm({...gastoForm, fecha: e.target.value})} /></div>
                <div><Label>Monto *</Label><Input type="number" value={gastoForm.monto} onChange={e => setGastoForm({...gastoForm, monto: parseFloat(e.target.value) || 0})} /></div>
              </div>
              <div><Label>Concepto *</Label><Input value={gastoForm.concepto} onChange={e => setGastoForm({...gastoForm, concepto: e.target.value})} placeholder="Descripción del gasto" /></div>
              <div><Label>Categoría *</Label>
                <Select value={gastoForm.categoria} onValueChange={v => setGastoForm({...gastoForm, categoria: v})}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>{categorias.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Beneficiario</Label><Input value={gastoForm.beneficiario} onChange={e => setGastoForm({...gastoForm, beneficiario: e.target.value})} /></div>
                <div><Label>No. Factura</Label><Input value={gastoForm.factura_numero} onChange={e => setGastoForm({...gastoForm, factura_numero: e.target.value})} /></div>
              </div>
              
              {/* Comprobante Fiscal */}
              <div>
                <Label>Comprobante Fiscal</Label>
                <Select value={gastoForm.tiene_comprobante_fiscal} onValueChange={v => setGastoForm({...gastoForm, tiene_comprobante_fiscal: v, comercio: v === "no" ? "" : gastoForm.comercio, ncf: v === "no" ? "" : gastoForm.ncf})}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no">No</SelectItem>
                    <SelectItem value="si">Sí</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Campos adicionales si tiene comprobante fiscal */}
              {gastoForm.tiene_comprobante_fiscal === "si" && (
                <div className="space-y-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-blue-800 dark:text-blue-300">Comercio *</Label>
                      <Input 
                        value={gastoForm.comercio} 
                        onChange={e => setGastoForm({...gastoForm, comercio: e.target.value})} 
                        placeholder="Nombre del comercio"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-blue-800 dark:text-blue-300"># de NCF *</Label>
                      <Input 
                        value={gastoForm.ncf} 
                        onChange={e => setGastoForm({...gastoForm, ncf: e.target.value})} 
                        placeholder="Ej: B0100000001"
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-blue-800 dark:text-blue-300">Transacción</Label>
                    <Input 
                      value={gastoForm.transaccion} 
                      onChange={e => setGastoForm({...gastoForm, transaccion: e.target.value})} 
                      placeholder="Número de transacción"
                      className="mt-1"
                      data-testid="gasto-transaccion"
                    />
                  </div>
                </div>
              )}
              
              <div><Label>Descripción</Label><Textarea value={gastoForm.descripcion} onChange={e => setGastoForm({...gastoForm, descripcion: e.target.value})} /></div>
              
              {/* Invoice Upload */}
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-4">
                <Label className="flex items-center gap-2 mb-2"><Upload className="w-4 h-4" />Imagen de Factura (Opcional)</Label>
                <Input 
                  type="file" 
                  accept="image/*,.pdf"
                  onChange={handleFacturaSelect}
                  className="cursor-pointer"
                />
                {facturaPreview && (
                  <div className="mt-3 relative">
                    <img src={facturaPreview} alt="Preview" className="max-h-32 rounded border" />
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="absolute top-0 right-0 text-red-500"
                      onClick={() => { setFacturaFile(null); setFacturaPreview(null); }}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}
                {facturaFile && !facturaPreview && (
                  <div className="mt-2 text-sm text-slate-600 flex items-center gap-2">
                    <Image className="w-4 h-4" />
                    {facturaFile.name}
                    <Button variant="ghost" size="sm" onClick={() => setFacturaFile(null)}><X className="w-4 h-4" /></Button>
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowGastoDialog(false)}>Cancelar</Button>
              <Button onClick={handleGasto} disabled={saving || uploading} className="bg-[#0C314C] text-white">
                {(saving || uploading) && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {uploading ? "Subiendo..." : "Registrar"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reposicion Dialog */}
        <Dialog open={showReposicionDialog} onOpenChange={setShowReposicionDialog}>
          <DialogContent>
            <DialogHeader><DialogTitle><RefreshCw className="w-5 h-5 inline mr-2" />Reponer Caja Chica</DialogTitle></DialogHeader>
            <div className="space-y-4 py-4">
              <div><Label>Monto a Reponer *</Label><Input type="number" value={reposicionForm.monto} onChange={e => setReposicionForm({...reposicionForm, monto: parseFloat(e.target.value) || 0})} placeholder="0.00" /></div>
              <div><Label>Número de Cheque</Label><Input value={reposicionForm.numero_cheque} onChange={e => setReposicionForm({...reposicionForm, numero_cheque: e.target.value})} /></div>
              <div><Label>Observaciones</Label><Textarea value={reposicionForm.observaciones} onChange={e => setReposicionForm({...reposicionForm, observaciones: e.target.value})} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowReposicionDialog(false)}>Cancelar</Button>
              <Button onClick={handleReposicion} disabled={saving} className="bg-green-600 hover:bg-green-700 text-white">{saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Reponer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Categorias Dialog */}
        <Dialog open={showCategoriasDialog} onOpenChange={setShowCategoriasDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle><Tag className="w-5 h-5 inline mr-2" />Gestionar Categorías</DialogTitle></DialogHeader>
            <div className="space-y-4 py-4">
              {/* Add new category */}
              <div className="flex gap-2">
                <Input 
                  placeholder="Nueva categoría" 
                  value={nuevaCategoria.label}
                  onChange={e => setNuevaCategoria({...nuevaCategoria, label: e.target.value})}
                />
                <Button onClick={handleCreateCategoria} className="bg-[#0C314C] text-white">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              
              {/* List categories */}
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {categorias.map(cat => (
                  <div key={cat.id || cat.value} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                    <span>{cat.label}</span>
                    {cat.id && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-red-500 h-7 w-7 p-0"
                        onClick={() => handleDeleteCategoria(cat.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCategoriasDialog(false)}>Cerrar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Gasto Dialog */}
        <Dialog open={showEditDialog} onOpenChange={(open) => {
          setShowEditDialog(open);
          if (!open) { setEditingGasto(null); setEditForm({}); }
        }}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle><Pencil className="w-5 h-5 inline mr-2" />Editar Gasto</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Fecha *</Label>
                  <Input type="date" value={editForm.fecha || ""} onChange={e => setEditForm({...editForm, fecha: e.target.value})} />
                </div>
                <div>
                  <Label>Monto *</Label>
                  <Input type="number" value={editForm.monto || 0} onChange={e => setEditForm({...editForm, monto: parseFloat(e.target.value) || 0})} />
                </div>
              </div>
              <div>
                <Label>Concepto *</Label>
                <Input value={editForm.concepto || ""} onChange={e => setEditForm({...editForm, concepto: e.target.value})} placeholder="Descripción del gasto" />
              </div>
              <div>
                <Label>Categoría *</Label>
                <Select value={editForm.categoria || ""} onValueChange={v => setEditForm({...editForm, categoria: v})}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>{categorias.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Beneficiario</Label>
                  <Input value={editForm.beneficiario || ""} onChange={e => setEditForm({...editForm, beneficiario: e.target.value})} />
                </div>
                <div>
                  <Label>No. Factura</Label>
                  <Input value={editForm.factura_numero || ""} onChange={e => setEditForm({...editForm, factura_numero: e.target.value})} />
                </div>
              </div>
              
              {/* Comprobante Fiscal */}
              <div>
                <Label>Comprobante Fiscal</Label>
                <Select value={editForm.tiene_comprobante_fiscal || "no"} onValueChange={v => setEditForm({...editForm, tiene_comprobante_fiscal: v, comercio: v === "no" ? "" : editForm.comercio, ncf: v === "no" ? "" : editForm.ncf})}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no">No</SelectItem>
                    <SelectItem value="si">Sí</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Campos adicionales si tiene comprobante fiscal */}
              {editForm.tiene_comprobante_fiscal === "si" && (
                <div className="space-y-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-blue-800 dark:text-blue-300">Comercio *</Label>
                      <Input 
                        value={editForm.comercio || ""} 
                        onChange={e => setEditForm({...editForm, comercio: e.target.value})} 
                        placeholder="Nombre del comercio"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-blue-800 dark:text-blue-300"># de NCF *</Label>
                      <Input 
                        value={editForm.ncf || ""} 
                        onChange={e => setEditForm({...editForm, ncf: e.target.value})} 
                        placeholder="Ej: B0100000001"
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-blue-800 dark:text-blue-300">Transacción</Label>
                    <Input 
                      value={editForm.transaccion || ""} 
                      onChange={e => setEditForm({...editForm, transaccion: e.target.value})} 
                      placeholder="Número de transacción"
                      className="mt-1"
                      data-testid="edit-gasto-transaccion"
                    />
                  </div>
                </div>
              )}
              
              <div>
                <Label>Descripción</Label>
                <Textarea value={editForm.descripcion || ""} onChange={e => setEditForm({...editForm, descripcion: e.target.value})} />
              </div>
              
              {/* Invoice Upload Section for Edit */}
              <div className="border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg p-4">
                <Label className="flex items-center gap-2 mb-2"><Upload className="w-4 h-4" />Imagen de Factura</Label>
                
                {/* Show existing invoice if present */}
                {editingGasto?.factura_url && !editFacturaFile && (
                  <div className="mb-3 p-2 bg-green-50 dark:bg-green-900/20 rounded border border-green-200 dark:border-green-700">
                    <p className="text-sm text-green-700 dark:text-green-300 flex items-center gap-2">
                      <Image className="w-4 h-4" />
                      Factura existente guardada
                      <a 
                        href={editingGasto.factura_url.startsWith('/api/') ? `${API.replace('/api', '')}${editingGasto.factura_url}` : `${API}${editingGasto.factura_url}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline ml-2"
                      >
                        Ver
                      </a>
                    </p>
                  </div>
                )}
                
                <Input 
                  type="file" 
                  accept="image/*,.pdf"
                  onChange={handleEditFacturaSelect}
                  className="cursor-pointer"
                />
                <p className="text-xs text-slate-500 mt-1">
                  {editingGasto?.factura_url ? "Seleccione un archivo para reemplazar la factura existente" : "Seleccione un archivo para agregar una factura"}
                </p>
                
                {editFacturaPreview && (
                  <div className="mt-3 relative">
                    <img src={editFacturaPreview} alt="Preview" className="max-h-32 rounded border" />
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="absolute top-0 right-0 text-red-500"
                      onClick={() => { setEditFacturaFile(null); setEditFacturaPreview(null); }}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}
                {editFacturaFile && !editFacturaPreview && (
                  <div className="mt-2 text-sm text-slate-600 flex items-center gap-2">
                    <Image className="w-4 h-4" />
                    {editFacturaFile.name}
                    <Button variant="ghost" size="sm" onClick={() => setEditFacturaFile(null)}><X className="w-4 h-4" /></Button>
                  </div>
                )}
              </div>
              
              {editingGasto?.updated_at && (
                <p className="text-xs text-slate-500">
                  Última edición: {new Date(editingGasto.updated_at).toLocaleString('es-DO')} por {editingGasto.updated_by_name || "Sistema"}
                </p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancelar</Button>
              <Button onClick={handleSaveEdit} disabled={saving || editUploading} className="bg-[#0C314C] text-white">
                {(saving || editUploading) && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {editUploading ? "Subiendo..." : "Guardar Cambios"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Authorization Modal */}
        <AuthorizationModal
          isOpen={showAuthModal}
          onClose={() => {
            setShowAuthModal(false);
            setPendingDeleteGastoId(null);
          }}
          onAuthorized={executeDeleteGastoWithAuth}
          title="Autorización para Eliminar Gasto"
          description="Para eliminar este gasto de caja chica, ingrese las credenciales de un usuario autorizado."
          getAuthHeaders={getAuthHeaders}
        />
      </main>
    </div>
  );
}
